import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import Spinner from '@/common/components/Spinner'
import DynamicSectionForm from '../../../DynamicSectionForm'
import { DynamicSectionFormValues } from '../../../DynamicSectionForm/components/FormSchema'
import useQueryGetDynamicSectionById from '@/modules/client-setting/api/hooks/useQueryGetDynamicSectionById'
import useMutationUpdateDynamicSection from '@/modules/client-setting/api/hooks/useMutationUpdateDynamicSection'
import { DynamicSectionQueryKeys } from '@/modules/client-setting/keys'

export default function EditDynamicSectionForm() {
	const navigate = useNavigate()
	const { id } = useParams()
	const queryClient = useQueryClient()

	const { data: dynamicSection, isLoading } = useQueryGetDynamicSectionById(id!)

	const { mutate: updateDynamicSection } = useMutationUpdateDynamicSection({
		onSuccess: () => {
			toast.success('แก้ไข Section สำเร็จ')
			queryClient.invalidateQueries({
				queryKey: [DynamicSectionQueryKeys.GetDynamicSectionById, id],
			})
			navigate('/operator/dynamic-section')
		},
		onError: (error) => {
			showErrorToast(error.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง')
		},
	})

	const handleSubmit = (values: DynamicSectionFormValues) => {
		// Clean up the data to match API expectations

		updateDynamicSection({
			id: id!,
			payload: {
				...values,
				lottoAnnouncementSection: values.lottoAnnouncementSection
					? {}
					: undefined,
				leaderboardSection: values.leaderboardSection
					? {
							...values.leaderboardSection,
							limit: values.leaderboardSection.limit ?? 5,
						}
					: undefined,
				gameSuggestionSection: values.gameSuggestionSection
					? {
							...values.gameSuggestionSection,
							limit: values.gameSuggestionSection.limit ?? 6,
						}
					: undefined,
				footballMatchesSection: values.footballMatchesSection
					? {
							...values.footballMatchesSection,
							gameId: values.footballMatchesSection.gameId || undefined,
						}
					: undefined,
			},
		})
	}

	if (isLoading || !dynamicSection) return <Spinner />

	const defaultValues: Partial<DynamicSectionFormValues> = {
		name: dynamicSection.name,
		type: dynamicSection.type,
		gameListSection: dynamicSection.gameListSection
			? {
					title: dynamicSection.gameListSection.title,
					link: dynamicSection.gameListSection.link,
					gameType: dynamicSection.gameListSection.gameType,
					gameListType: dynamicSection.gameListSection.gameListType,
					filterProviderIds:
						dynamicSection.gameListSection.filterProviders?.map((p) => p.id) ||
						[],
					gameIds: dynamicSection.gameListSection.gameIds || [],
				}
			: undefined,
		lottoAnnouncementSection: dynamicSection.lottoAnnouncementSection,
		leaderboardSection: dynamicSection.leaderboardSection,
		gameSuggestionSection: dynamicSection.gameSuggestionSection,
		footballMatchesSection: dynamicSection.footballMatchesSection
			? {
					title: dynamicSection.footballMatchesSection.title,
					gameId: dynamicSection.footballMatchesSection.gameId ?? '',
					showLive: dynamicSection.footballMatchesSection.showLive,
					showUpcoming: dynamicSection.footballMatchesSection.showUpcoming,
					showFinished: dynamicSection.footballMatchesSection.showFinished,
				}
			: undefined,
	}

	return (
		<DynamicSectionForm
			mode="edit"
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
		/>
	)
}
